/**
 * voice-v8 — wen die Human-Design-Auswahlen anbieten. (24.09.2026)
 *
 * Martin: *„Man muss lernen, zwischen wirklichen Usern und
 * Human-Design-Anfragen zu unterscheiden und nicht vermischt.“* Die beiden
 * Auswahlen im HD-Blatt („Wessen Chart anzeigen“ und die Verbindung zweier
 * Charts) listeten jede Zeile von /api/admin/users in einem Topf: echte
 * Konten neben den Profilen bekannter Personen, dazu nie benutzte und
 * technische Konten.
 *
 * Jetzt Gruppen (<optgroup>): „Nutzer“ (Kontoart 'nutzer'), „Nie benutzt“
 * ('unbenutzt') und „Human-Design-Profile“ ('profil'). Technische Konten
 * (App-Prüfung, Tests) stehen nicht darin.
 *
 * ⚠️ 24.09.2026, Prüfung: Hier fehlten auch die nie benutzten Konten, mit der
 *    Begründung „kein Chart, das man ansehen will“. Nachgesehen an der echten
 *    Datenbank (nur lesend): drei nie benutzte Konten tragen Geburtsdaten und
 *    Chart-Ergebnisse, eines einen Teilen-Link. Ohne sie in der Auswahl wäre
 *    ihr Chart in der Oberfläche nicht mehr erreichbar gewesen — auf
 *    /admin/charts stehen nur Profile. Sie stehen darum in einer eigenen
 *    Gruppe, getrennt von den Menschen, die die App benutzen.
 *
 * Die Kontoart liefert der Server mit jeder Zeile (memory/users.ts, kontoArt);
 * hier wird nichts nachgerechnet.
 */

export interface HdKonto {
  id: number;
  name?: string | null;
  email?: string;
  /** Die Kontoart vom Server; fehlt sie (ältere Antwort), gilt „nutzer“. */
  art?: string;
}

/** Die drei Gruppen, je nach Name sortiert. */
export function hdGruppen(
  konten: readonly HdKonto[],
  ohne?: number,
): { nutzer: HdKonto[]; unbenutzt: HdKonto[]; profile: HdKonto[] } {
  const name = (k: HdKonto) => k.name?.trim() || k.email || `Nutzer ${k.id}`;
  const nachName = (a: HdKonto, b: HdKonto) => name(a).localeCompare(name(b), "de");
  const liste = konten.filter((k) => k.id !== ohne);
  const art = (a: string) => liste.filter((k) => (k.art ?? "nutzer") === a).sort(nachName);
  return { nutzer: art("nutzer"), unbenutzt: art("unbenutzt"), profile: art("profil") };
}

/**
 * Hängt die Gruppen als <optgroup> an eine Auswahl. Eine leere Gruppe
 * entfällt. `gewaehlt` markiert eine Nummer als ausgewählt.
 */
export function fuelleHdAuswahl(
  sel: HTMLSelectElement,
  konten: readonly HdKonto[],
  opts: { ohne?: number; gewaehlt?: number } = {},
): void {
  const g = hdGruppen(konten, opts.ohne);
  for (const [titel, liste] of [
    ["Nutzer", g.nutzer],
    ["Nie benutzt", g.unbenutzt],
    ["Human-Design-Profile", g.profile],
  ] as const) {
    if (liste.length === 0) continue;
    const gruppe = document.createElement("optgroup");
    gruppe.label = titel;
    for (const k of liste) {
      const o = document.createElement("option");
      o.value = String(k.id);
      o.textContent = k.name?.trim() || k.email || `Nutzer ${k.id}`;
      gruppe.appendChild(o);
    }
    sel.appendChild(gruppe);
  }
  /*
   * Erst wählen, wenn die Optionen in der Auswahl stehen: `selected` an einer
   * losen Option übernimmt nicht jede Umgebung beim Einhängen. Steht die
   * Nummer nicht darin (etwa „ich“, oben), bleibt die Auswahl, wie sie war.
   */
  const ziel = opts.gewaehlt;
  if (ziel !== undefined && [...g.nutzer, ...g.unbenutzt, ...g.profile].some((k) => k.id === ziel)) sel.value = String(ziel);
}
